import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import FeeScheduleSchema, { FeeScheduleJSON, FeeScheduleParams } from "work/work-agreements/fee-schedules/fee-schedule-schema";

export type AnyFeeScheduleCategory =
  | FeeScheduleCategory
  | FeeScheduleCategoryMaskedCurrency;

export default class FeeSchedule {
  public static create(values: FeeScheduleParams): FeeSchedule {
    const parsed = FeeScheduleSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid FeeSchedule parameters.");
    }
    return new FeeSchedule(parsed);
  }

  public static fromJSON(json: FeeScheduleJSON): FeeSchedule {
    if (isNullOrUndefined(json)) return json;
    const params = FeeScheduleSchema.deserialize(json);
    return new FeeSchedule(params);
  }

  public static fromJSONArray(jsonArray: FeeScheduleJSON[]): FeeSchedule[] {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return jsonArray.map(json => FeeSchedule.fromJSON(json));
  }

  public static splitCategoriesByDuplicate(
    existingCategories: Array<AnyFeeScheduleCategory>,
    incomingCategories: Array<AnyFeeScheduleCategory>
  ) {
    return incomingCategories.reduce<{
      duplicateCategories: Array<AnyFeeScheduleCategory>;
      uniqueCategories: Array<AnyFeeScheduleCategory>;
    }>(
      (result, category) => {
        const isDuplicate = [...existingCategories, ...result.uniqueCategories]
          .some((existingCategory) => existingCategory.isEqualTo(category));

        if (isDuplicate) {
          result.duplicateCategories.push(category);
        } else {
          result.uniqueCategories.push(category);
        }

        return result;
      },
      {
        duplicateCategories: [],
        uniqueCategories: [],
      }
    );
  }

  public name?: string;
  public categories: AnyFeeScheduleCategory[] = [];

  public constructor(params: FeeScheduleParams) {
    this.name = params.name;
    this.categories = params.categories.map(cat => {
      if (cat.currencyIsMasked) {
        return new FeeScheduleCategoryMaskedCurrency({ ...cat });
      }
      return new FeeScheduleCategory(cat);
    });
  }

  public toJSON(): FeeScheduleJSON {
    return FeeScheduleSchema.serialize(this);
  }
}
